// Purveyor slice P1 (docs/2026-09-27-purveyor-design.md): the directory,
// read side. Deliberately read-only -- no add/edit/delete controls anywhere
// on this page. Writes happen only via scripts/seed-purveyors.mjs until a
// later slice gives purveyors their own accounts (P4).
//
// Loaded directly here, not through MealPlannerContext: the directory is
// global reference data, not scoped to the signed-in household, so it has
// no business living alongside the household-keyed state everything else in
// that context carries.
import { useEffect, useMemo, useState } from "react";
import { loadPurveyorDirectory, loadHouseholdSources, addHouseholdSource, removeHouseholdSource, loadInventoryBoxIds, addBoxToInventory } from "../lib/db";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { CATEGORY_LABELS, COLORS, FONT_SIZE, SPACE, RADIUS } from "../lib/constants";
import { capitalizeFirst, formatQuantity, getWeekStartDate, toISODate, shiftWeek, formatWeekRange } from "../lib/utils";

const TYPE_TINTS = {
  CSA: [COLORS.accentTint, COLORS.accent],
  REKO: [COLORS.plumTint, COLORS.plumInk],
  Vendor: [COLORS.skyTint, COLORS.skyInk],
  "Farmers Market": [COLORS.amberTintDeep, COLORS.amberInkDeep],
  "Farm Stand": [COLORS.clayTint, COLORS.clayInk],
  "Country Store": [COLORS.rustTint, COLORS.danger],
};

function TypeBadges({ types }) {
  return (
    <>
      {(types || []).map((t) => {
        const [bg, fg] = TYPE_TINTS[t] || [COLORS.stoneTint, COLORS.ink];
        return (
          <span key={t} className="mp-sans" style={{ background: bg, color: fg, padding: "var(--space-hair) var(--space-md)", borderRadius: RADIUS.pill, fontSize: FONT_SIZE.xs, fontWeight: 600 }}>
            {t}
          </span>
        );
      })}
    </>
  );
}

function locationLine(loc) {
  const parts = [loc.city, loc.state].filter(Boolean);
  return parts.length ? parts.join(", ") : "";
}

function amountLine(item) {
  if (!(item.quantity > 0)) return "";
  return `${formatQuantity(item.quantity)}${item.unit ? ` ${item.unit}` : ""}`;
}

export function PurveyorsPage() {
  const { householdId, planDefaults, colors } = useMealPlanner();
  const { card, ink, soft, accent, line } = colors;
  const isMobile = useIsMobile();

  const [purveyors, setPurveyors] = useState(null); // null = still loading
  const [sourceIds, setSourceIds] = useState(new Set());
  const [addedBoxIds, setAddedBoxIds] = useState(new Set());
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);
  const [sourcesOnly, setSourcesOnly] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const [sourceBusyId, setSourceBusyId] = useState(null);
  const [addBoxBusyId, setAddBoxBusyId] = useState(null);
  const [addBoxErrorId, setAddBoxErrorId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadPurveyorDirectory(), loadHouseholdSources(householdId), loadInventoryBoxIds(householdId)])
      .then(([rows, sources, boxIds]) => {
        if (cancelled) return;
        setPurveyors(rows);
        setSourceIds(new Set(sources));
        setAddedBoxIds(boxIds);
      })
      .catch((e) => {
        console.error("[PurveyorsPage] load failed:", e);
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  // Box -> inventory (P3): an explicit action, not automatic. Optimistic
  // "added" state on success; on failure the box stays addable and the error
  // is surfaced next to it, the same shape as toggleSource's rollback.
  async function handleAddBoxToInventory(purveyor, box, weekStart) {
    setAddBoxBusyId(box.id);
    setAddBoxErrorId(null);
    try {
      await addBoxToInventory(householdId, purveyor, box, weekStart);
      setAddedBoxIds((prev) => new Set(prev).add(box.id));
    } catch (e) {
      console.error("[PurveyorsPage] addBoxToInventory failed:", e);
      setAddBoxErrorId(box.id);
    } finally {
      setAddBoxBusyId(null);
    }
  }

  // Tagging is "this household uses this purveyor" -- collaborative, like
  // Inventory, not owner-only. Optimistic with rollback on failure, the same
  // shape as every other toggle in this app (e.g. toggleFavorite).
  async function toggleSource(purveyorId) {
    const isTagged = sourceIds.has(purveyorId);
    setSourceBusyId(purveyorId);
    setSourceIds((prev) => {
      const next = new Set(prev);
      if (isTagged) next.delete(purveyorId);
      else next.add(purveyorId);
      return next;
    });
    try {
      if (isTagged) await removeHouseholdSource(householdId, purveyorId);
      else await addHouseholdSource(householdId, purveyorId);
    } catch (e) {
      console.error("[PurveyorsPage] toggleSource failed:", e);
      setSourceIds((prev) => {
        const next = new Set(prev);
        if (isTagged) next.add(purveyorId);
        else next.delete(purveyorId);
        return next;
      });
    } finally {
      setSourceBusyId(null);
    }
  }

  const byId = useMemo(() => new Map((purveyors || []).map((p) => [p.id, p])), [purveyors]);

  const allTypes = useMemo(() => {
    const set = new Set();
    (purveyors || []).forEach((p) => p.purveyorTypes.forEach((t) => set.add(t)));
    return [...set].sort();
  }, [purveyors]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (purveyors || []).filter((p) => {
      if (sourcesOnly && !sourceIds.has(p.id)) return false;
      if (typeFilter && !p.purveyorTypes.includes(typeFilter)) return false;
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || locationLine(p.location).toLowerCase().includes(q);
    });
  }, [purveyors, query, typeFilter, sourcesOnly, sourceIds]);

  const inputStyle = { boxSizing: "border-box", padding: "var(--space-sm) var(--space-md)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, color: ink, fontSize: FONT_SIZE.base, fontFamily: "var(--font-sans)" };
  const viewing = viewingId ? byId.get(viewingId) : null;

  if (loadError) {
    return <p className="mp-sans" style={{ color: colors.danger }}>Couldn't load the purveyor directory — try reloading the page.</p>;
  }
  if (purveyors === null) {
    return <p className="mp-sans" style={{ color: soft }}>Loading the purveyor directory…</p>;
  }

  if (viewing) {
    return (
      <PurveyorDetail
        purveyor={viewing}
        byId={byId}
        onBack={() => setViewingId(null)}
        colors={colors}
        isMobile={isMobile}
        isSource={sourceIds.has(viewing.id)}
        sourceBusy={sourceBusyId === viewing.id}
        onToggleSource={() => toggleSource(viewing.id)}
        planDefaults={planDefaults}
        addedBoxIds={addedBoxIds}
        addBoxBusyId={addBoxBusyId}
        addBoxErrorId={addBoxErrorId}
        onAddBoxToInventory={(box, weekStart) => handleAddBoxToInventory(viewing, box, weekStart)}
      />
    );
  }

  return (
    <div>
      <p className="mp-sans" style={{ fontSize: FONT_SIZE.sm, color: soft, marginBottom: SPACE.xl2, maxWidth: 640 }}>
        Local CSAs, farmers markets, farm stands, country stores, REKO circles, and vendors. Browsable only — purveyors are added and kept up to date by the app admin. Tag the ones your household actually uses as a source.
      </p>

      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: SPACE.md, marginBottom: SPACE.xl2 }}>
        <input style={{ ...inputStyle, flex: 1 }} placeholder="Search purveyors…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <button
          className="mp-btn mp-sans"
          onClick={() => setSourcesOnly((s) => !s)}
          style={{ background: sourcesOnly ? accent : "transparent", color: sourcesOnly ? COLORS.onAccent : soft, border: `1px solid ${line}`, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm, flexShrink: 0 }}
        >
          ★ My sources{sourceIds.size > 0 ? ` (${sourceIds.size})` : ""}
        </button>
      </div>

      {allTypes.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.sm, marginBottom: SPACE.xl2 }}>
          <button
            className="mp-btn mp-sans"
            onClick={() => setTypeFilter(null)}
            style={{ background: typeFilter === null ? accent : "transparent", color: typeFilter === null ? COLORS.onAccent : soft, border: `1px solid ${line}`, padding: "var(--space-hair) var(--space-lg)", borderRadius: RADIUS.pill, fontSize: FONT_SIZE.sm }}
          >
            All
          </button>
          {allTypes.map((t) => (
            <button
              key={t}
              className="mp-btn mp-sans"
              onClick={() => setTypeFilter(t)}
              style={{ background: typeFilter === t ? accent : "transparent", color: typeFilter === t ? COLORS.onAccent : soft, border: `1px solid ${line}`, padding: "var(--space-hair) var(--space-lg)", borderRadius: RADIUS.pill, fontSize: FONT_SIZE.sm }}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="mp-sans" style={{ color: soft }}>
          {purveyors.length === 0 ? "No purveyors in the directory yet." : sourcesOnly ? "You haven't tagged any sources yet." : "No purveyors match that search."}
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
          {filtered.map((p) => {
            const isSource = sourceIds.has(p.id);
            return (
              <div key={p.id} style={{ display: "flex", alignItems: "stretch", gap: SPACE.sm }}>
                <button
                  onClick={() => setViewingId(p.id)}
                  className="mp-sans"
                  style={{ flex: 1, textAlign: "left", background: card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: isMobile ? "var(--space-lg)" : "var(--space-lg) var(--space-xxl)", cursor: "pointer", color: ink }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: SPACE.md, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: FONT_SIZE.md }}>{p.name}</span>
                    <span style={{ display: "flex", gap: SPACE.xs, flexWrap: "wrap" }}>
                      <TypeBadges types={p.purveyorTypes} />
                    </span>
                  </div>
                  {locationLine(p.location) && <div style={{ fontSize: FONT_SIZE.sm, color: soft, marginTop: SPACE.hair }}>{locationLine(p.location)}</div>}
                  {p.description && <div style={{ fontSize: FONT_SIZE.sm, color: soft, marginTop: SPACE.sm }}>{p.description}</div>}
                </button>
                <button
                  onClick={() => toggleSource(p.id)}
                  disabled={sourceBusyId === p.id}
                  title={isSource ? "Remove from my sources" : "Tag as a source"}
                  aria-label={isSource ? `Remove ${p.name} from my sources` : `Tag ${p.name} as a source`}
                  style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, color: isSource ? colors.accent : soft, fontSize: FONT_SIZE.h3, padding: "0 var(--space-lg)", cursor: "pointer", flexShrink: 0 }}
                >
                  {isSource ? "★" : "☆"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PurveyorDetail({ purveyor: p, byId, onBack, colors, isMobile, isSource, sourceBusy, onToggleSource, planDefaults, addedBoxIds, addBoxBusyId, addBoxErrorId, onAddBoxToInventory }) {
  const { card, ink, soft, accent, line } = colors;
  const sectionStyle = { marginBottom: SPACE.xl2 };
  const headingStyle = { fontSize: FONT_SIZE.sm, fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: SPACE.sm };

  const vendors = (p.vendorIds || []).map((id) => byId.get(id)).filter(Boolean);
  const venues = (p.venueIds || []).map((id) => byId.get(id)).filter(Boolean);
  const hoursByDay = {};
  (p.hours || []).forEach((h) => (hoursByDay[h.dayOfWeek] = hoursByDay[h.dayOfWeek] || []).push(h));

  return (
    <div>
      <button className="mp-btn mp-sans" onClick={onBack} style={{ background: "transparent", border: "none", color: accent, padding: 0, marginBottom: SPACE.xl2, fontSize: FONT_SIZE.sm, cursor: "pointer" }}>
        ← Back to directory
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: SPACE.md, flexWrap: "wrap", marginBottom: SPACE.md }}>
        <h2 style={{ fontSize: FONT_SIZE.h3, margin: 0 }}>{p.name}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: SPACE.md, flexWrap: "wrap" }}>
          <span style={{ display: "flex", gap: SPACE.xs, flexWrap: "wrap" }}>
            <TypeBadges types={p.purveyorTypes} />
          </span>
          <button
            className="mp-btn mp-sans"
            onClick={onToggleSource}
            disabled={sourceBusy}
            style={{ background: isSource ? colors.accent : "transparent", color: isSource ? COLORS.onAccent : accent, border: `1px solid ${isSource ? colors.accent : line}`, padding: "var(--space-hair) var(--space-lg)", borderRadius: RADIUS.pill, fontSize: FONT_SIZE.sm, fontWeight: 600 }}
          >
            {isSource ? "★ My source" : "☆ Tag as a source"}
          </button>
        </div>
      </div>
      {p.description && <p className="mp-sans" style={{ color: soft, maxWidth: 640, marginBottom: SPACE.xl2 }}>{p.description}</p>}

      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: SPACE.x3 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {(p.location.address || p.location.city) && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Location</div>
              <div className="mp-sans" style={{ color: ink }}>
                {p.location.address && <div>{p.location.address}</div>}
                <div>{[p.location.city, p.location.state, p.location.zip].filter(Boolean).join(", ")}</div>
              </div>
            </div>
          )}

          {(p.contactInfo.phone || p.contactInfo.email || p.contactInfo.website || p.contactInfo.socialUrl) && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Contact</div>
              <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.hair, color: ink }}>
                {p.contactInfo.phone && <span>{p.contactInfo.phone}</span>}
                {p.contactInfo.email && <a href={`mailto:${p.contactInfo.email}`} style={{ color: accent }}>{p.contactInfo.email}</a>}
                {p.contactInfo.website && <a href={p.contactInfo.website} target="_blank" rel="noreferrer" style={{ color: accent }}>{p.contactInfo.website}</a>}
                {p.contactInfo.socialUrl && <a href={p.contactInfo.socialUrl} target="_blank" rel="noreferrer" style={{ color: accent }}>{p.contactInfo.socialUrl}</a>}
              </div>
            </div>
          )}

          {p.pickupLocations.length > 0 && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Pickup locations</div>
              {p.pickupLocations.map((l) => (
                <div key={l.id} className="mp-sans" style={{ color: ink, marginBottom: SPACE.xs }}>
                  {l.address && <div>{l.address}</div>}
                  <div style={{ color: soft, fontSize: FONT_SIZE.sm }}>{[l.city, l.state, l.zip].filter(Boolean).join(", ")}</div>
                </div>
              ))}
            </div>
          )}

          {Object.keys(hoursByDay).length > 0 && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Hours</div>
              {Object.entries(hoursByDay).map(([day, windows]) => (
                <div key={day} className="mp-sans" style={{ display: "flex", gap: SPACE.md, color: ink, marginBottom: SPACE.hair }}>
                  <span style={{ minWidth: 90, fontWeight: 600 }}>{day}</span>
                  <span style={{ color: soft }}>
                    {windows.map((w) => `${w.openTime || "?"}–${w.closeTime || "?"}`).join(", ")}
                    {windows[0]?.seasonStart && windows[0]?.seasonEnd ? ` (${windows[0].seasonStart} – ${windows[0].seasonEnd})` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}

          {venues.length > 0 && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Sells at</div>
              <div className="mp-sans" style={{ color: ink }}>{venues.map((v) => v.name).join(", ")}</div>
            </div>
          )}
          {vendors.length > 0 && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Vendors</div>
              <div className="mp-sans" style={{ color: ink }}>{vendors.map((v) => v.name).join(", ")}</div>
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {p.products.length > 0 && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Products</div>
              <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-md) var(--space-lg)" }}>
                {p.products.map((pr) => (
                  <div key={pr.id} className="mp-sans" style={{ display: "flex", justifyContent: "space-between", gap: SPACE.md, padding: "var(--space-xs) 0", borderBottom: `1px dashed ${line}` }}>
                    <span style={{ color: ink }}>
                      {capitalizeFirst(pr.name)}
                      {pr.category ? <span style={{ color: soft }}> · {pr.category}</span> : null}
                    </span>
                    <span style={{ color: pr.availability === "sold out" ? colors.danger : soft, fontSize: FONT_SIZE.sm, textAlign: "right", flexShrink: 0 }}>
                      {pr.availability === "available" ? amountLine(pr) || CATEGORY_LABELS[pr.groceryCategory] : pr.availability}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {p.boxes.length > 0 && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Boxes</div>
              {p.boxes.map((b) => (
                <div key={b.id} style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-md) var(--space-lg)", marginBottom: SPACE.md }}>
                  <div className="mp-sans" style={{ display: "flex", justifyContent: "space-between", fontWeight: 600, marginBottom: SPACE.xs }}>
                    <span>{b.name || "Box"}{b.deliveryDate ? ` — ${b.deliveryDate}` : ""}</span>
                    <span style={{ color: soft, fontSize: FONT_SIZE.sm }}>{b.status}</span>
                  </div>
                  {b.items.map((it) => {
                    const product = it.productId ? p.products.find((pr) => pr.id === it.productId) : null;
                    const label = it.snapshotName || product?.name || it.oneOffName || "(item)";
                    return (
                      <div key={it.id} className="mp-sans" style={{ display: "flex", justifyContent: "space-between", fontSize: FONT_SIZE.sm, color: soft }}>
                        <span>{capitalizeFirst(label)}</span>
                        <span>{amountLine(it)}</span>
                      </div>
                    );
                  })}
                  {b.items.length > 0 && (
                    <AddBoxToInventory
                      box={b}
                      planDefaults={planDefaults}
                      isAdded={addedBoxIds.has(b.id)}
                      busy={addBoxBusyId === b.id}
                      hasError={addBoxErrorId === b.id}
                      onAdd={(weekStart) => onAddBoxToInventory(b, weekStart)}
                      colors={colors}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {p.products.length === 0 && p.boxes.length === 0 && (
            <p className="mp-sans" style={{ color: soft }}>No catalog or boxes listed yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Purveyor slice P3: the explicit "add this box to my inventory" action.
// Defaults to the week containing the box's deliveryDate (falling back to
// today's week if the box has none), overridable with the same prev/next
// week controls InventoryPage itself uses -- picked once per box, not reset
// on every render, so nudging the week doesn't snap back after a re-render.
function AddBoxToInventory({ box, planDefaults, isAdded, busy, hasError, onAdd, colors }) {
  const { soft, accent, danger, line } = colors;
  const [weekStart, setWeekStart] = useState(() => toISODate(getWeekStartDate(box.deliveryDate || new Date(), planDefaults.weekStartDay)));

  if (isAdded) {
    return (
      <div className="mp-sans" style={{ fontSize: FONT_SIZE.sm, color: accent, marginTop: SPACE.sm, fontWeight: 600 }}>
        ✓ Added to inventory
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: SPACE.sm, marginTop: SPACE.md, flexWrap: "wrap" }}>
      <button className="mp-btn" onClick={() => setWeekStart((w) => shiftWeek(w, -1))} style={{ background: "transparent", border: `1px solid ${line}`, color: soft, padding: "var(--space-hair) var(--space-md)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }} aria-label="Earlier week">
        ←
      </button>
      <span className="mp-sans" style={{ fontSize: FONT_SIZE.sm, color: soft, minWidth: 130, textAlign: "center" }}>Week of {formatWeekRange(weekStart)}</span>
      <button className="mp-btn" onClick={() => setWeekStart((w) => shiftWeek(w, 1))} style={{ background: "transparent", border: `1px solid ${line}`, color: soft, padding: "var(--space-hair) var(--space-md)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }} aria-label="Later week">
        →
      </button>
      <button
        className="mp-btn mp-sans"
        onClick={() => onAdd(weekStart)}
        disabled={busy}
        style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-hair) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm, fontWeight: 600 }}
      >
        Add to inventory
      </button>
      {hasError && <span className="mp-sans" style={{ color: danger, fontSize: FONT_SIZE.sm }}>Couldn't add — try again.</span>}
    </div>
  );
}
